/**
 * The relationship types an applicant can pick when inviting a voucher.
 *
 * Lives here rather than inline in `ApplicantFlow` so the recruiting/leasing
 * split is unit-testable without mounting the flow.
 */

export interface RelationshipOption {
  value: string;
  label: string;
}

/**
 * The full set, and the only set the backend accepts.
 *
 * ⚠️ Must stay a subset of `VALID_RELATIONSHIPS` in the platform repo
 * (`services/digavouch/src/api/routes/vouchers.ts`). The invite endpoint 400s
 * an unknown relationship, so nothing may be added here alone.
 */
export const ALL_RELATIONSHIP_OPTIONS: RelationshipOption[] = [
  { value: "landlord", label: "Landlord" },
  { value: "employer", label: "Employer" },
  { value: "colleague", label: "Colleague" },
  { value: "professor", label: "Professor" },
  { value: "friend", label: "Friend" },
  { value: "other", label: "Other" },
];

/**
 * A recruiting flow drops "Landlord" — a leasing relationship that reads as a
 * mistake when the vouch is supporting a job application.
 *
 * **Keyed off the org's VERTICAL, not the org name — and no longer off the
 * flow plan.** This used to read `ApplicantFlow`'s `definitionPlan !== null`,
 * which was the best proxy available before `organizations.industry` reached
 * the front end. It asks "does this session run a workflow definition?", and
 * dropping "Landlord" is not an answer to that: a leasing org given a bespoke
 * flow would lose the relationship its vouches most rely on.
 * `isRecruiting` in `ApplicantFlow` now resolves `session.org_industry`, and
 * still names no org in the front end.
 *
 * Rockwell is `residential_leasing` and keeps all six, unchanged. IDW is
 * `hr_recruiting` and gets the five, unchanged — the two proxies agree on
 * every org that exists today, which is why this swap renders identically.
 *
 * ## Deliberately a NARROWING, not a re-mapping
 *
 * Every value returned here is already accepted and weighted by the backend,
 * so this ships front-end-only. Adding a recruiting-native value
 * (`recruiter`, `manager`, `classmate`) is NOT a front-end change and must not
 * be done here:
 *
 *   - `VALID_RELATIONSHIPS` rejects an unknown value with a 400; and
 *   - `RELATIONSHIP_BASE_STRENGTH[relationship] ?? 0.40` floors an unrecognised
 *     value at the LOWEST weight in the table — below `friend` (0.50) — so a
 *     "Hiring Manager" vouch would silently score as the weakest kind there is.
 *
 * Relabelling is similarly not free: the raw value, not this label, is what the
 * voucher sees on their confirmation screen (`VoucherFlow`, `capitalize`) and
 * what the operator sees in the inquiry timeline.
 *
 * ⚠️ Weights remain leasing-shaped and global — `landlord` 0.90 is the top of
 * the table, so a recruiting flow's strongest available relationship is
 * `employer` at 0.85. Per-flow relationship weighting is not expressible today;
 * that is platform #360 (C.1v), with ADR-014 §D6 as the eventual home.
 */
export function relationshipOptionsFor(recruiting: boolean): RelationshipOption[] {
  if (!recruiting) return ALL_RELATIONSHIP_OPTIONS;
  return ALL_RELATIONSHIP_OPTIONS.filter((opt) => opt.value !== "landlord");
}

/**
 * The `qa_responses` key under which a voucher's OWN account of the
 * relationship is recorded (Juan, 2026-10-15). The voucher may change what the
 * applicant picked on the first screen; that answer is recorded and shown,
 * and never scored: the invitation's `relationship`, which the backend weights
 * (`RELATIONSHIP_BASE_STRENGTH`), stays as the applicant set it.
 * `qa_responses` is stored and returned to operators and read by no scoring.
 * Fixed text (no name in it) so readers can find it.
 */
export const STATED_RELATIONSHIP_KEY = "Relationship to the applicant, as the voucher states it";

/** The label for a relationship value, or the value itself when unknown. */
export function relationshipLabel(value: string | null | undefined): string {
  if (!value) return "";
  return ALL_RELATIONSHIP_OPTIONS.find((o) => o.value === value.toLowerCase())?.label ?? value;
}

/** The value for a stored relationship label or value ("Employer" -> "employer"). */
export function relationshipValue(labelOrValue: string | null | undefined): string {
  if (!labelOrValue) return "";
  const lower = labelOrValue.toLowerCase();
  return ALL_RELATIONSHIP_OPTIONS.find((o) => o.value === lower || o.label.toLowerCase() === lower)?.value ?? lower;
}

// The voucher's own "In what context do you know them?" question (former
// Step 4 Q6) was dropped va#177: it duplicated the read-only relationship the
// applicant already supplied when inviting this voucher, shown to the
// voucher on Step 0 ("Your relationship to {first}"). `ALL_VOUCH_CONTEXT_OPTIONS`
// and `vouchContextOptionsFor` used to live here and are removed with it —
// nothing else read them.
